import imageCompression from "browser-image-compression";
import { Field, Form, Formik } from "formik";
import { useEffect, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import { useIntl } from "react-intl";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { ImageCompressConfig } from "../../../constants/ImageCompressConfig";
import { ApiStatusTypeEnum } from "../../../enumeration/ApiStatusTypeEnum";
import { useAuth } from "../../auth/components/AuthInit";
import { Input, TextArea } from "../../common/control/InputField";
import { getCookie } from "../../slidebar/components/DarkMode";
import * as actions from "../redux/MessageBoxAction";
import { CreatePostSchema } from "../schema/CreatePostSchema";

export const CreateMessageBox = ({ setSwalProps }) => {
  const { auth } = useAuth();
  const intl = useIntl();
  const dispatch = useDispatch();
  const [isShow, setIsShow] = useState(false);
  const [mode, setMode] = useState(getCookie("selectedTheme"));
  const formikRef = useRef();
  const fileInputRef = useRef();
  const [file, setFile] = useState();
  const [imagePreview, setImagePreview] = useState();
  const showTopSection = false;

  const messageBoxState = useSelector(
    ({ messageBox }) => messageBox,
    shallowEqual
  );

  const handleClose = () => setIsShow(false);

  useEffect(() => {
    const handle = () => {
      setMode((prev) => (prev === "dark" ? "light" : "dark"));
    };

    window.addEventListener("setTheme", handle);

    return () => {
      window.removeEventListener("setTheme", handle);
    };
  }, []);

  useEffect(() => {
    if (isShow) {
      dispatch(actions.getInboxUI({ action: "createnewinboxUI" }));
      setFile(undefined);
      setImagePreview(undefined);
      formikRef.current?.resetForm();
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }, [isShow]);

  const handleFileChange = (event) => {
    const handleFile = (file, fileName) => {
      const newFile = new File([file], fileName, {
        type: file.type,
      });
      setImagePreview(URL.createObjectURL(newFile));
      setFile(newFile);
    };
    const fileName = event.currentTarget.files[0].name;
    imageCompression(event.currentTarget.files[0], ImageCompressConfig)
      .then((res) => handleFile(res, fileName))
      .catch(() => handleFile(event.currentTarget.files[0], fileName));
  };

  const createPost = (values) => {
    const imagename = file
      ? `${Date.now()}.${file.name.split(".").at(-1)}`
      : "";

    const newQueryParams = {
      action: "createnewinbox",
      agentid: auth.selagentid ?? auth.agentid,
      loginid: auth.selloginid ?? auth.loginid,
      projectid: auth.selprojectid ?? auth.projectid,
      imagename,
      ...values,
    };

    const formData = new FormData();
    formData.append("imagename", imagename);
    formData.append("image", file);
    formData.append("jsonData", JSON.stringify(newQueryParams));
    dispatch(actions.createPost(formData)).then((resp) => {
      resp = resp.data;

      if (parseInt(resp.code) === ApiStatusTypeEnum.SUCCESS) {
        setIsShow(false);
        dispatch(actions.getMemberInboxList({ action: "memberinboxlist" }));
        setSwalProps({
          show: true,
          icon: "success",
          title: intl.formatMessage({ id: "success" }),
          text: intl.formatMessage({
            id: "createSuccessfully",
          }),
        });
      } else {
        setSwalProps({
          show: true,
          icon: "error",
          title: intl.formatMessage({ id: "error" }),
          text: resp.message,
        });
      }
    });
  };

  return (
    <>
      <button
        type="button"
        className="btn btn-primary"
        onClick={() => setIsShow(true)}
      >
        {intl.formatMessage({ id: "createPost" })}
      </button>
      <Modal
        show={isShow}
        onHide={handleClose}
        centered
        data-bs-theme={mode}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {intl.formatMessage({
              id: "createPost",
            })}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {messageBoxState.inboxUI?.code === "0" ? (
            <Formik
              initialValues={{
                category: "normalpost",
                showtype: "both",
                title: "",
                content: "",
                memberusername: "",
              }}
              validationSchema={CreatePostSchema}
              innerRef={formikRef}
              enableReinitialize={true}
              onSubmit={createPost}
            >
              {({
                values,
                handleSubmit,
                setFieldValue,
                touched,
                setTouched,
                errors,
                setFieldError,
              }) => (
                <Form>
                  {showTopSection && (
                    <div className="row mb-0">
                      <div className="col-md-6 mb-2">
                        <label className="mb-2 text-input-label">
                          {intl.formatMessage({ id: "type" })}
                        </label>
                        <select
                          className="form-select"
                          name="showtype"
                          onChange={(e) => {
                            setTouched({});
                            setFieldValue("showtype", e.target.value);
                          }}
                          value={values.showtype}
                        >
                          {messageBoxState.inboxUI.inboxShowTypeLists.map(
                            (inboxShowTypeList) => (
                              <option
                                value={inboxShowTypeList.code}
                                key={inboxShowTypeList.code}
                              >
                                {inboxShowTypeList.description}
                              </option>
                            )
                          )}
                        </select>
                      </div>
                      <div className="col-md-6 mb-2">
                        <label className="mb-2 text-input-label">
                          {intl.formatMessage({ id: "category" })}
                        </label>
                        <select
                          className="form-select"
                          name="category"
                          onChange={(e) =>
                            setFieldValue("category", e.target.value)
                          }
                          value={values.category}
                        >
                          {messageBoxState.inboxUI.inboxCategoryLists.map(
                            (inboxCategoryList) => (
                              <option
                                value={inboxCategoryList.code}
                                key={inboxCategoryList.code}
                              >
                                {inboxCategoryList.description}
                              </option>
                            )
                          )}
                        </select>
                      </div>
                    </div>
                  )}
                  {(values.showtype === "both" ||
                    values.showtype === "textonly") && (
                    <>
                      <div className="mb-2">
                        <label className="mb-2 text-input-label">
                          {intl.formatMessage({ id: "title" })}
                        </label>
                        <Field
                          name="title"
                          component={Input}
                          placeholder={intl.formatMessage({ id: "title" })}
                          autoComplete="off"
                          type="text"
                          inputClass="form-control"
                          isAlphanumericOnly={false}
                        />
                      </div>
                      <div className="mb-2">
                        <label className="mb-2 text-input-label">
                          {intl.formatMessage({ id: "content" })}
                        </label>
                        <Field
                          name="content"
                          component={TextArea}
                          placeholder={intl.formatMessage({ id: "content" })}
                          autoComplete="off"
                          inputClass="form-control"
                          isAlphanumericOnly={false}
                          rows={10}
                        />
                      </div>
                    </>
                  )}
                  {(values.showtype === "both" ||
                    values.showtype === "imageonly") && (
                    <div className="mb-2">
                      <label className="mb-2 text-input-label">
                        {intl.formatMessage({ id: "image" })}
                      </label>
                      {imagePreview && imagePreview !== "" && (
                        <div
                          style={{ maxHeight: "200px" }}
                          className="mb-2 position-relative"
                        >
                          <img
                            src={imagePreview}
                            style={{ maxHeight: "200px" }}
                            onError={() => setImagePreview(undefined)}
                          />

                          <i
                            className="position-absolute bi bi-x-circle-fill text-danger fs-5 cursor-pointer"
                            onClick={() => {
                              setImagePreview(undefined);
                              setFile(undefined);
                              fileInputRef.current.value = "";
                            }}
                            style={{ top: "0px", left: "5px" }}
                          />
                        </div>
                      )}

                      <input
                        type="file"
                        className="form-control text-font2"
                        placeholder={intl.formatMessage({ id: "image" })}
                        autoComplete="off"
                        accept="image/*"
                        onChange={handleFileChange}
                        ref={fileInputRef}
                      />
                    </div>
                  )}
                  <div className="mb-2">
                    <label className="mb-2 text-input-label">
                      {intl.formatMessage({ id: "memberUsernameOptional" })}
                    </label>
                    <Field
                      name="memberusername"
                      component={Input}
                      placeholder={intl.formatMessage({
                        id: "memberUsernameOptional",
                      })}
                      autoComplete="off"
                      type="text"
                      inputClass="form-control"
                      isAlphanumericOnly={false}
                    />
                  </div>
                </Form>
              )}
            </Formik>
          ) : (
            messageBoxState.inboxUI?.message && (
              <div className="text-center">
                {messageBoxState.inboxUI?.message}
              </div>
            )
          )}
        </Modal.Body>
        {messageBoxState.inboxUI?.code === "0" && (
          <Modal.Footer>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => formikRef.current?.handleSubmit()}
            >
              {intl.formatMessage({ id: "create" })}
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleClose}
            >
              {intl.formatMessage({ id: "close" })}
            </button>
          </Modal.Footer>
        )}
      </Modal>
    </>
  );
};
